import "@/App.css";
import { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import CookieBanner from "@/components/CookieBanner";
import LandingPage from "@/pages/LandingPage";
import AuthPage from "@/pages/AuthPage";
import AuthCallback from "@/pages/AuthCallback";
import Dashboard from "@/pages/Dashboard";
import CalendarView from "@/pages/CalendarView";
import Clients from "@/pages/Clients";
import ClientDetail from "@/pages/ClientDetail";
import PrivacyPolicy from "@/pages/PrivacyPolicy";
import CookiePolicy from "@/pages/CookiePolicy";
import Contatti from "@/pages/Contatti";
import AdminPage from "@/pages/AdminPage";
import PremiumPage from "@/pages/PremiumPage";
import StatisticsPage from "@/pages/StatisticsPage";
import BusinessCardsPage from "@/pages/BusinessCardsPage";
import ExcelUploadPage from "@/pages/ExcelUploadPage";
import ResetPasswordPage from "@/pages/ResetPasswordPage";
import ManagerDashboard from "@/pages/ManagerDashboard";
import MapsPage from "@/pages/MapsPage";
import { registerSW, scheduleReminders } from "@/utils/notifications";

// Redirect smart: se loggato → /app, altrimenti → landing
function RootRedirect() {
  const { user, loading } = useAuth();
  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="text-white/40 text-sm">Caricamento...</div>
    </div>
  );
  if (user) return <Navigate to="/app" replace />;
  return <LandingPage />;
}

function ProtectedApp() {
  const { user, loading } = useAuth();
  useEffect(() => { registerSW().then(() => scheduleReminders()); }, []);

  if (loading) return (
    <div className="min-h-screen bg-[#0F172A] flex items-center justify-center">
      <div className="text-white/40 text-sm">Caricamento...</div>
    </div>
  );
  if (!user) return <Navigate to="/auth" replace />;
  return (
    <Layout>
      <Routes>
        <Route path="/" element={<Dashboard />} />
        <Route path="/calendario" element={<CalendarView />} />
        <Route path="/clienti" element={<Clients />} />
        <Route path="/clienti/:id" element={<ClientDetail />} />
      </Routes>
    </Layout>
  );
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
        </AuthProvider>
      </BrowserRouter>
      <Toaster richColors position="top-left" toastOptions={{ duration: 2000 }} />
    </div>
  );
}

// Intercetta session_id nell'hash DURANTE il render (sincrono) per evitare race conditions
function AppRouter() {
  const location = useLocation();
  // Se Google Auth sta tornando con session_id nell'hash → mostra AuthCallback direttamente
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/privacy-policy" element={<PrivacyPolicy />} />
      <Route path="/cookie-policy" element={<CookiePolicy />} />
      <Route path="/contatti" element={<Contatti />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="/app/premium" element={<Layout><PremiumPage /></Layout>} />
      <Route path="/app/statistiche" element={<Layout><StatisticsPage /></Layout>} />
      <Route path="/app/mappa" element={<Layout fullHeight><MapsPage /></Layout>} />
      <Route path="/app/biglietti" element={<Layout><BusinessCardsPage /></Layout>} />
      <Route path="/app/import-excel" element={<Layout><ExcelUploadPage /></Layout>} />
      <Route path="/app/manager" element={<Layout><ManagerDashboard /></Layout>} />
      <Route path="/app/*" element={<ProtectedApp />} />
    </Routes>
  );
}

export default App;
